// Day and night (port of the Godot SkyController cycle, browser look). The daytime frame is the
// golden-haze sky exactly as it was; the cycle only ever blends AWAY from it and back:
//
//   sunrise -> long golden day (the sun holds the art-directed ~25 degree key) -> sunset (fire on
//   the sun's side, rose belt and violet overhead) -> night (indigo dome, stars, a nebula band, and
//   the painted planets and moons from the Godot game; the big moon becomes the key light) -> dawn.
//
// Everything is uniforms: no rebuild, no allocation per frame. The clock is the server tick, so
// every cowboy in a posse sees the same sky. URL overrides for checks: ?tod=0..1 freezes the
// phase (0.3 day, 0.6 sunset, 0.78 night, 0.97 dawn), ?daylen=<seconds> sets the cycle length.

import * as THREE from 'three';
import { HAZE } from '../materials';

/** Seconds for one full day. Godot used 600 s; a run sees roughly one night. */
export const DAY_LENGTH = 600;
/** Where a fresh room starts: the golden day, just after the sun reaches its key. */
const START_PHASE = 0.14;

const lin = (hex: number) => new THREE.Color(hex);
const D2R = Math.PI / 180;

/** Sun path keys: phase, elevation (deg), azimuth offset from the art-directed day key (deg).
 *  The day is a long low arc that never climbs past the key, so daylight keeps today's look. */
const SUN_KEYS: readonly (readonly [number, number, number])[] = [
  [0.0, -7, -78], [0.05, 4, -52], [0.13, 1000, -8], [0.5, 1000, 4], [0.585, 3, 12], [0.645, -9, 18],
  [0.8, -38, -10], [0.95, -13, -70], [1.0, -7, -78],
];

/** The bodies of the night sky (atlas cells in assets/sky/celestial_atlas.webp, 4x2, built from
 *  the Godot game's Assets/textures/sky/celestial). Position is elevation/azimuth in the sky frame
 *  (azimuth measured from the day sun's azimuth), size is the angular diameter. */
export const BODIES = [
  // the gas giant hanging over the far side of the valley, opposite the sun: "this is not Earth"
  { cell: 0, el: 19, az: 152, size: 31, dayVis: 0.2, soft: 0.42, atmo: 0.55, name: 'giant' },
  // the big tan moon: the night key light comes from it
  { cell: 1, el: 27, az: 190, size: 8.5, dayVis: 0, soft: 0.12, atmo: 0, name: 'moon' },
  { cell: 2, el: 36, az: 62, size: 4.4, dayVis: 0, soft: 0.1, atmo: 0, name: 'moon2' },
  { cell: 3, el: 15, az: 119, size: 6.5, dayVis: 0, soft: 0.3, atmo: 0.45, name: 'cream' }, // warm: ties the night back to the gold
  { cell: 4, el: 15, az: -38, size: 3.4, dayVis: 0, soft: 0.3, atmo: 0.3, name: 'teal' },
  { cell: 5, el: 47, az: 238, size: 2.6, dayVis: 0, soft: 0.3, atmo: 0.3, name: 'ember' },
  { cell: 6, el: 71, az: 300, size: 3.1, dayVis: 0, soft: 0.3, atmo: 0.3, name: 'storm' },
  { cell: 7, el: 23, az: 286, size: 3.6, dayVis: 0, soft: 0.1, atmo: 0, name: 'rust' },
] as const;
const KEY_MOON = 1;

/** The art-directed day key's azimuth (materials.ts SUN_MEADOW): the sky frame is built around it. */
const DAY_AZ = Math.atan2(-0.72, -0.55);
export function skyDir(elDeg: number, azDeg: number, out: THREE.Vector3) {
  const e = elDeg * D2R, a = DAY_AZ + azDeg * D2R;
  return out.set(Math.cos(e) * Math.cos(a), Math.sin(e), Math.cos(e) * Math.sin(a));
}

// palette of the cycle (linear, converted from sRGB hex once)
const C = {
  duskHaze: lin(0xe3a183), duskLow: lin(0xe9b08f), duskSunHaze: lin(0xff9150),
  nightHaze: lin(0x1d3044), nightLow: lin(0x22374c), moonHaze: lin(0x86a6c8),
  sunDay: lin(0xffe6b0), sunLow: lin(0xff9a52), moonLight: lin(0xa8c4ff),
  hemiSkyDay: lin(0xffd9a0), hemiGroundDay: lin(0x3e5a4e),
  hemiSkyDusk: lin(0xe8a693), hemiGroundDusk: lin(0x4c4058),
  hemiSkyNight: lin(0x5f88b0), hemiGroundNight: lin(0x1a2c30),
  tintDusk: new THREE.Color(0.95, 0.66, 0.66), tintNight: new THREE.Color(0.15, 0.22, 0.31),
};

// read at load: the game rewrites the URL (room link) before the world view exists
const QS = typeof location !== 'undefined' ? new URLSearchParams(location.search) : null;
const TOD = QS?.get('tod');
const FROZEN = TOD != null && TOD !== '' && Number.isFinite(Number(TOD)) ? ((Number(TOD) % 1) + 1) % 1 : null;
const DAYLEN = Number(QS?.get('daylen')) > 0 ? Number(QS?.get('daylen')) : DAY_LENGTH;

const smooth = (a: number, b: number, x: number) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };

export class DayNight {
  /** 0..1 through the day (see SUN_KEYS) */
  phase = START_PHASE;
  /** 0 day .. 1 full night */
  night = 0;
  /** sunset/sunrise glow 0..1 */
  dusk = 0;
  /** the true sun direction (sky disc, light shafts) */
  readonly sunDisc = new THREE.Vector3();
  /** shared uniforms for the sky dome, the celestial bodies, the backdrop and the motes */
  readonly U = {
    uNight: { value: 0 },
    uDusk: { value: 0 },
    uSunDisc: { value: new THREE.Vector3() },
    /** sky frame -> world (stars, nebula and bodies turn slowly through the night) */
    uSkyRot: { value: new THREE.Matrix3() },
    uSkyRotInv: { value: new THREE.Matrix3() },
    /** backdrop relight: multiply and haze-lift for the pre-fogged ranges, mist and clouds */
    uSkyTint: { value: new THREE.Color(1, 1, 1) },
    uSkyLift: { value: 0 },
  };
  private frozen: number | null = FROZEN;
  private dayLen = DAYLEN;
  private base = { sunI: 2.6, hemiI: 1.25, shafts: -1, warmth: 1 };
  private post: { material: THREE.ShaderMaterial; sunDir: THREE.Vector3 | null } | null = null;
  private m4 = new THREE.Matrix4();
  private pole = new THREE.Vector3(0.16, 0.95, 0.27).normalize();
  private moon = new THREE.Vector3();

  constructor() {
    this.setClock(0);
  }

  /** Seconds on the shared clock (server ticks / TICK_RATE). */
  setClock(seconds: number) {
    this.phase = this.frozen ?? (((START_PHASE + seconds / this.dayLen) % 1) + 1) % 1;
  }

  bindPost(post: { material: THREE.ShaderMaterial; sunDir: THREE.Vector3 | null }) {
    this.post = post;
    post.sunDir = this.sunDisc;
    this.base.shafts = post.material.uniforms.uShafts.value as number;
  }

  /**
   * Blend the world toward the current time of day. Call AFTER setMood(), which writes the day
   * values (sun key, haze) this starts from, every frame.
   */
  apply(sun: THREE.DirectionalLight | null, hemi: THREE.HemisphereLight | null) {
    const p = this.phase;
    // ---- sun path
    const day = HAZE.uSunDir.value as THREE.Vector3; // setMood's day key (meadow ~25 deg, town ~41)
    const dayEl = Math.asin(Math.min(1, Math.max(-1, day.y))) / D2R;
    let k = 0;
    while (k < SUN_KEYS.length - 2 && p > SUN_KEYS[k + 1][0]) k++;
    const [p0, e0r, a0] = SUN_KEYS[k], [p1, e1r, a1] = SUN_KEYS[k + 1];
    const t = smooth(p0, p1, p);
    const e0 = e0r > 900 ? dayEl : e0r, e1 = e1r > 900 ? dayEl : e1r;
    const el = e0 + (e1 - e0) * t, az = a0 + (a1 - a0) * t;
    skyDir(el, az, this.sunDisc);
    this.U.uSunDisc.value.copy(this.sunDisc);

    // ---- the sky turns a little through the night around a tilted pole
    const rot = 0.45 * Math.sin((p - 0.78) * Math.PI * 2);
    this.m4.makeRotationAxis(this.pole, rot);
    this.U.uSkyRot.value.setFromMatrix4(this.m4);
    this.U.uSkyRotInv.value.copy(this.U.uSkyRot.value).transpose();
    const b = BODIES[KEY_MOON];
    skyDir(b.el, b.az, this.moon).applyMatrix3(this.U.uSkyRot.value).normalize();

    // ---- phases of light
    const night = smooth(1, -9, el);
    // the glow peaks with the sun on the horizon and lingers a little after it sets
    const dusk = smooth(16, 3, el) * smooth(-17, -2, el);
    this.night = night; this.dusk = dusk;
    this.U.uNight.value = night; this.U.uDusk.value = dusk;

    // key light: the sun by day, the big moon by night; they swap with the sun on the horizon,
    // where both are dark, so the shadow direction never visibly jumps
    const sunUp = el > 0;
    const sunStr = smooth(-1, 14, el);
    HAZE.uSunDir.value.copy(sunUp ? this.sunDisc : this.moon);
    if (sun) {
      if (sunUp) sun.color.copy(C.sunLow).lerp(C.sunDay, smooth(2, 18, el)), sun.intensity = this.base.sunI * sunStr;
      else sun.color.copy(C.moonLight), sun.intensity = 2.6 * night;
      sun.position.copy(sun.target.position).addScaledVector(HAZE.uSunDir.value, 140);
    }
    if (hemi) {
      hemi.color.copy(C.hemiSkyDay).lerp(C.hemiSkyDusk, dusk).lerp(C.hemiSkyNight, night);
      hemi.groundColor.copy(C.hemiGroundDay).lerp(C.hemiGroundDusk, dusk).lerp(C.hemiGroundNight, night);
      hemi.intensity = this.base.hemiI * (1 - 0.12 * dusk) * (1 - 0.4 * night);
    }

    // ---- haze (setMood already wrote the day colours this frame)
    const hc = HAZE.uHazeColor.value as THREE.Color, lc = HAZE.uLowHazeColor.value as THREE.Color, sc = HAZE.uSunHazeColor.value as THREE.Color;
    // the twilight keeps some of its warmth into the first and last of the night
    const nk = night * (1 - 0.55 * dusk);
    hc.lerp(C.duskHaze, dusk * 0.8).lerp(C.nightHaze, nk);
    lc.lerp(C.duskLow, dusk * 0.8).lerp(C.nightLow, nk);
    sc.copy(C.sunDay).lerp(C.duskSunHaze, dusk).lerp(C.moonHaze, night);
    HAZE.uHazeDensity.value *= 1 - 0.18 * night;
    HAZE.uCloudShadow.value *= 1 - night;
    HAZE.uNight.value = night;
    this.U.uSkyTint.value.setRGB(1, 1, 1).lerp(C.tintDusk, dusk * (1 - night)).lerp(C.tintNight, night);
    this.U.uSkyLift.value = 0.12 * dusk + 0.65 * night;

    // ---- post: shafts only from a visible sun, the golden grade relaxes at night
    if (this.post) {
      const u = this.post.material.uniforms;
      u.uShafts.value = this.base.shafts * smooth(-1, 6, el);
      u.uWarmth.value = this.base.warmth * (1 - 0.65 * night);
    }
  }
}
